/**
 * Contrôle d'accès d'une console d'ÉQUIPE après « Continuer avec Google ».
 *
 * RÈGLE : dans une console d'équipe, Google ne CRÉE jamais d'accès. Il ne fait que prouver qu'une personne possède une
 * adresse e-mail. Il se rattache (GoTrue, liaison automatique) à un compte DÉJÀ existant portant la même adresse
 * VÉRIFIÉE ; un compte Google inconnu naît sans aucun rôle, aucune organisation, aucun droit. La console vérifie donc,
 * APRÈS le retour, l'autorisation réelle (membre d'organisation, administrateur de la plateforme, rôle d'équipe,
 * `hub_peut`…) et, à défaut, DÉCONNECTE aussitôt, avec UNE seule phrase, la même pour tous les cas : elle ne dit ni si
 * l'adresse existe ailleurs, ni sous quel rôle.
 *
 * Module pur : pas de `window`, testable.
 */

export const MESSAGE_REFUS_GOOGLE = "Ce compte Google n'a pas accès à cette console. Demandez une invitation."
export const MESSAGE_VERIFICATION_IMPOSSIBLE = "La vérification de l'accès n'a pas pu se faire. Réessayez dans un instant."

interface SessionMinimale {
  access_token?: string | undefined
  user?: { app_metadata?: { provider?: unknown } | undefined } | null | undefined
}

/** Charge utile d'un jeton signé (JWT), sans vérifier la signature : sert seulement à LIRE le mode d'ouverture. */
function chargeUtile(jeton: string): Record<string, unknown> | null {
  const morceau = jeton.split('.')[1]
  if (!morceau) return null
  try {
    const b64 = morceau.replace(/-/g, '+').replace(/_/g, '/')
    const texte = atob(b64 + '='.repeat((4 - (b64.length % 4)) % 4))
    const objet: unknown = JSON.parse(new TextDecoder().decode(Uint8Array.from(texte, (c) => c.charCodeAt(0))))
    return objet && typeof objet === 'object' ? (objet as Record<string, unknown>) : null
  } catch {
    return null
  }
}

/**
 * La session a-t-elle été OUVERTE par un fournisseur externe (Google) ?
 *
 * Le jeton d'accès porte `amr` (« méthodes d'authentification ») : `oauth` quand la session vient d'un retour Google. Ce
 * marqueur suit la session à travers les renouvellements, y compris quand le navigateur est rouvert. Repli : un compte
 * créé par Google (`app_metadata.provider = google`). Un compte e-mail qui s'est connecté par mot de passe n'a PAS `oauth`.
 */
export function sessionViaGoogle(session: SessionMinimale | null | undefined): boolean {
  if (!session) return false
  const charge = session.access_token ? chargeUtile(session.access_token) : null
  const amr = charge?.['amr']
  if (Array.isArray(amr) && amr.some((m) => (m as { method?: unknown } | null)?.method === 'oauth')) return true
  return session.user?.app_metadata?.provider === 'google'
}

export type Verdict<A> = { ok: true; acces: A } | { ok: false; message: string }

/**
 * Applique le verdict d'accès d'une console.
 *
 * • `evaluer()` pose la question à la base (la console fournit sa propre règle : organisation, administrateur, rôle…) ;
 * • verdict `ok` : la personne entre ;
 * • verdict refusé : déconnexion IMMÉDIATE. Pour une session Google, le message est toujours `MESSAGE_REFUS_GOOGLE` ;
 * • `evaluer()` échoue (réseau, fonction absente…) : une session Google est déconnectée aussi (on échoue FERMÉ — on ne
 *   laisse pas entrer quelqu'un qu'on n'a pas pu vérifier) ; une autre session voit l'erreur remonter à l'appelant,
 *   comme avant.
 */
export async function controlerAcces<A>(p: {
  viaGoogle: boolean
  evaluer: () => Promise<Verdict<A>>
  deconnecter: () => Promise<void>
}): Promise<Verdict<A>> {
  const sortir = async () => {
    try {
      await p.deconnecter()
    } catch {
      /* La session locale est de toute façon abandonnée : on refuse quand même. */
    }
  }
  let verdict: Verdict<A>
  try {
    verdict = await p.evaluer()
  } catch (e) {
    if (!p.viaGoogle) throw e
    await sortir()
    return { ok: false, message: MESSAGE_VERIFICATION_IMPOSSIBLE }
  }
  if (verdict.ok) return verdict
  await sortir()
  return { ok: false, message: p.viaGoogle ? MESSAGE_REFUS_GOOGLE : verdict.message }
}
